import { decodePassengers } from 'utils/Reserbus';
import { MAX_QUICK_PURCHASE_PASSENGERS } from 'constants/quickPurchase';
import { getStoredPassengers } from './storedPassengers';

/**
 * Get available passengers for Quick Purchase.
 * Uses frequent passengers when available, falls back to localStorage stored passengers.
 * Sources are not combined to avoid duplicate entries.
 * @param {Array} frequentPassengers - Frequent passengers from Redux (normalized in setUpFrequentPassengers).
 * @param {object} [features] - Whitelabel features (passed to getStoredPassengers).
 * @returns {Array} Passenger objects from the selected source.
 */
export function getQuickPurchasePassengers(frequentPassengers, features) {
  const passengers = frequentPassengers || [];
  if (passengers.length > 0) return passengers;
  return getStoredPassengers(features);
}

/**
 * Count available passengers for Quick Purchase.
 * @param {Array} frequentPassengers - Frequent passengers from Redux.
 * @param {object} [features] - Whitelabel features.
 * @returns {number} Passenger count from the selected source.
 */
export function getQuickPurchasePassengerCount(frequentPassengers, features) {
  return getQuickPurchasePassengers(frequentPassengers, features).length;
}

/**
 * Get available passengers for Quick Purchase along with their source.
 * @param {Array} frequentPassengers - Frequent passengers from Redux.
 * @param {object} [features] - Whitelabel features.
 * @returns {{ passengers: Array, source: 'frequent' | 'local_storage' | 'none' }}
 */
export function getQuickPurchasePassengersWithSource(frequentPassengers, features) {
  const passengers = frequentPassengers || [];
  if (passengers.length > 0) return { passengers, source: 'frequent' };
  const stored = getStoredPassengers(features);
  if (stored.length > 0) return { passengers: stored, source: 'local_storage' };
  return { passengers: [], source: 'none' };
}

/**
 * Compute whether the current search context makes the user eligible for Quick Purchase.
 * Pure function — does not read the QUICK_PURCHASE_ENABLED experiment value.
 * The returned `isEligibleForQuickPurchase` is the value to expose as a GrowthBook
 * attribute so the experiment can target only eligible users.
 * @param {object} params - Eligibility inputs.
 * @param {string} params.searchPassengersEncoded - Encoded passengers string (e.g. 'A1').
 * @param {Array} params.frequentPassengers - Frequent passengers from Redux.
 * @param {object} params.features - Whitelabel features.
 * @param {Array} params.trips - Search result trips.
 * @param {boolean} params.isRoundTrip - Whether the search is round trip.
 * @param {boolean} params.isExchange - Whether the flow is an exchange.
 * @returns {{
 *   isEligibleForQuickPurchase: boolean,
 *   isOnlyAdults: boolean,
 *   isWithinPassengerLimit: boolean,
 *   hasEnoughPassengers: boolean,
 *   anyTripAllowsSeatSelection: boolean,
 *   qpPassengers: Array,
 *   passengersSource: 'frequent' | 'local_storage' | 'none',
 *   searchPassengerCount: number,
 * }}
 */
export function computeQuickPurchaseEligibility({
  searchPassengersEncoded,
  frequentPassengers,
  features,
  trips,
  isRoundTrip,
  isExchange,
}) {
  const decodedSearchPassengers = decodePassengers(searchPassengersEncoded || 'A1');
  const searchPassengerCount = decodedSearchPassengers.adult || 0;
  const isOnlyAdults =
    searchPassengerCount > 0 &&
    !decodedSearchPassengers.child &&
    !decodedSearchPassengers.minor &&
    !decodedSearchPassengers.infant;
  const isWithinPassengerLimit = searchPassengerCount <= MAX_QUICK_PURCHASE_PASSENGERS;
  const anyTripAllowsSeatSelection = Array.isArray(trips)
    ? trips.some((trip) => trip.allowsSeatSelection && !trip.hasConnections)
    : false;

  const { passengers: qpPassengers, source: passengersSource } =
    getQuickPurchasePassengersWithSource(frequentPassengers, features);

  const hasEnoughPassengers = qpPassengers.length >= searchPassengerCount;

  const isEligibleForQuickPurchase =
    isOnlyAdults &&
    isWithinPassengerLimit &&
    anyTripAllowsSeatSelection &&
    hasEnoughPassengers &&
    !isRoundTrip &&
    !isExchange;

  return {
    isEligibleForQuickPurchase,
    isOnlyAdults,
    isWithinPassengerLimit,
    hasEnoughPassengers,
    anyTripAllowsSeatSelection,
    qpPassengers,
    passengersSource,
    searchPassengerCount,
  };
}
